/**
 * Cursor helpers for the chat message list (TA-10150).
 *
 * The listMessages cache can contain edges whose cursors were never issued
 * by the backend: the messageUpdate subscription inserts confirmed messages
 * with a synthetic uuid cursor, optimistic/draft entries use numeric ids or
 * 'draft-cursor', and the custom listMessages merge lets a forward
 * (older-page) fetch overwrite pageInfo.startCursor with that older batch's
 * cursor. Pagination anchored on such a cursor makes the backend return a
 * mid-history slice — or nothing — so truly new messages never land.
 */

type CursorEdge = {
  cursor?: string | null;
  node?: {
    publishedAt?: string | null;
    createdAt?: string | null;
  } | null;
} | null;

/** True for cursors the backend never issued (see module doc). */
export const isPlaceholderCursor = (
  cursor: string | null | undefined
): boolean => {
  if (!cursor || typeof cursor !== 'string' || cursor.length === 0) {
    return false;
  }
  const digitsOnly = /^\d+$/;
  const uuidV4 =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return (
    digitsOnly.test(cursor) || uuidV4.test(cursor) || cursor === 'draft-cursor'
  );
};

/**
 * Newest message time (publishedAt/createdAt, epoch ms) across all edges,
 * regardless of cursor quality. -Infinity when there are no timestamped
 * edges. Used to decide whether a catch-up fetch is needed at all: when the
 * newest cached message is already at (or past) the chat's
 * recentActivityAt, the subscription delivered it and a redundant fetch
 * would only reorder edges and jump the scroll position (TA-10150).
 */
export const getNewestMessageTimeMs = (
  edges: readonly CursorEdge[] | null | undefined
): number => {
  let newest = -Infinity;
  (edges ?? []).forEach((edge) => {
    const node = edge?.node;
    const raw = node?.publishedAt || node?.createdAt;
    if (!raw) return;
    const time = new Date(raw).getTime();
    if (Number.isFinite(time) && time > newest) newest = time;
  });
  return newest;
};

/**
 * Whether the cached edges already cover the chat's recentActivityAt.
 *
 * IMPORTANT (TA-10150 QA follow-up): this only proves the NEWEST cached
 * message is at or past the activity timestamp — it says nothing about
 * completeness of the interval below it. During a burst, a later message
 * can be cached while an earlier one from the same burst is missing, and
 * this still returns true. Callers must therefore evaluate it AT THE
 * MOMENT a recentActivityAt bump is observed (before any later message
 * can mask the gap), not only when a debounced catch-up finally fires.
 * Returns false when recentActivityAt is absent/invalid so the caller
 * fetches rather than silently skipping.
 */
export const isCaughtUpToRecentActivity = (
  edges: readonly CursorEdge[] | null | undefined,
  recentActivityAt: string | null | undefined
): boolean => {
  const target = recentActivityAt ? new Date(recentActivityAt).getTime() : NaN;
  if (!Number.isFinite(target)) return false;
  return getNewestMessageTimeMs(edges) >= target;
};

/**
 * Cursor of the newest message (by publishedAt/createdAt) that carries a
 * real server-issued cursor — the safe anchor for a "fetch newer messages"
 * reverse pagination. Returns undefined when no edge qualifies (caller
 * should fall back to a full refetch).
 */
export const getNewestRealMessageCursor = (
  edges: readonly CursorEdge[] | null | undefined
): string | undefined => {
  let anchorCursor: string | undefined;
  let anchorTime = -Infinity;
  (edges ?? []).forEach((edge) => {
    const cursor = edge?.cursor;
    if (!cursor || isPlaceholderCursor(cursor)) return;
    const node = edge?.node;
    const time = new Date(node?.publishedAt || node?.createdAt || 0).getTime();
    if (time > anchorTime) {
      anchorTime = time;
      anchorCursor = cursor;
    }
  });
  return anchorCursor;
};
